import { create } from 'zustand';
import { APP_IDS, type AppId, type AppParams, type DesktopLayout, type WindowFrame } from '@omnia/shared';
import { APPS } from './registry.ts';

export interface WindowState {
  frame: WindowFrame;
  z: number;
  minimized: boolean;
  /** Frame to return to after an edge snap or maximize. */
  restoreFrame: WindowFrame | null;
  params: AppParams;
}

export type SnapZone = 'left' | 'right' | 'maximize';

export interface DeskBounds {
  width: number;
  height: number;
}

interface DesktopStore {
  bounds: DeskBounds;
  windows: Partial<Record<AppId, WindowState>>;
  focused: AppId | null;
  topZ: number;
  /** Bumped only by user edits so hydration does not echo back to the server. */
  revision: number;
  /** The saved layout has been applied; until then nothing is persisted. */
  hydrated: boolean;
  /** Windows holding work that closing or replacing would discard. */
  unsaved: Partial<Record<AppId, boolean>>;
  /** A request to show different content in a window with unsaved work, waiting for confirmation. */
  pendingOpen: { id: AppId; params: AppParams } | null;
  /** Height of the docked info strip above the desk; windows follow the desk's top edge when it changes. */
  deskTop: number;
  setBounds: (bounds: DeskBounds) => void;
  setDeskTop: (top: number) => void;
  hydrate: (layout: DesktopLayout | null) => void;
  /** Back to an empty desk, e.g. on sign-out so the next account never sees this one's windows. */
  reset: () => void;
  setUnsaved: (id: AppId, unsaved: boolean) => void;
  hasUnsaved: () => boolean;
  confirmPendingOpen: () => void;
  cancelPendingOpen: () => void;
  open: (id: AppId, params?: AppParams) => void;
  setParams: (id: AppId, params: AppParams) => void;
  close: (id: AppId) => void;
  focus: (id: AppId) => void;
  minimize: (id: AppId) => void;
  minimizeAll: () => void;
  toggleMaximize: (id: AppId) => void;
  setFrame: (id: AppId, frame: WindowFrame) => void;
  snap: (id: AppId, zone: SnapZone) => void;
  arrange: () => void;
}

export const GRID = 8;
export const snapToGrid = (value: number) => Math.round(value / GRID) * GRID;

export function snapFrame(zone: SnapZone, bounds: DeskBounds): WindowFrame {
  const gap = GRID;
  if (zone === 'maximize') return { x: gap, y: gap, width: bounds.width - gap * 2, height: bounds.height - gap * 2 };
  const width = Math.floor((bounds.width - gap * 3) / 2);
  return { x: zone === 'left' ? gap : gap * 2 + width, y: gap, width, height: bounds.height - gap * 2 };
}

/** Keeps at least the title bar reachable so a window can never be lost off-screen. */
export function clampFrame(id: AppId, frame: WindowFrame, bounds: DeskBounds): WindowFrame {
  const { minWidth, minHeight } = APPS[id];
  const width = Math.min(Math.max(frame.width, minWidth), Math.max(bounds.width, minWidth));
  const height = Math.min(Math.max(frame.height, minHeight), Math.max(bounds.height, minHeight));
  const x = Math.min(Math.max(frame.x, 96 - width), bounds.width - 96);
  const y = Math.min(Math.max(frame.y, 0), bounds.height - 44);
  return { x, y, width, height };
}

const SNAP_ZONES: SnapZone[] = ['maximize', 'left', 'right'];

function snapZoneOf(frame: WindowFrame, bounds: DeskBounds): SnapZone | null {
  return (
    SNAP_ZONES.find((zone) => {
      const snapped = snapFrame(zone, bounds);
      return snapped.x === frame.x && snapped.y === frame.y && snapped.width === frame.width && snapped.height === frame.height;
    }) ?? null
  );
}

const sameFrame = (a: WindowFrame, b: WindowFrame) => a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;

/** Maximized and half-screen windows keep filling the desk; free windows are pulled back into it. */
function refit(id: AppId, frame: WindowFrame, from: DeskBounds, to: DeskBounds): WindowFrame {
  const zone = snapZoneOf(frame, from);
  return zone ? snapFrame(zone, to) : clampFrame(id, frame, to);
}

function homeFrame(id: AppId, bounds: DeskBounds): WindowFrame {
  const { defaultWidth, defaultHeight, anchor } = APPS[id];
  const width = Math.min(defaultWidth, bounds.width - GRID * 4);
  const height = Math.min(defaultHeight, bounds.height - GRID * 4);
  return {
    x: snapToGrid(GRID * 2 + (bounds.width - width - GRID * 4) * anchor.x),
    y: snapToGrid(GRID * 2 + (bounds.height - height - GRID * 4) * anchor.y),
    width,
    height,
  };
}

export const useDesktop = create<DesktopStore>((set, get) => {
  const edit = (id: AppId, change: (window: WindowState) => Partial<WindowState>) =>
    set((state) => {
      const current = state.windows[id];
      if (!current) return state;
      return {
        windows: { ...state.windows, [id]: { ...current, ...change(current) } },
        revision: state.revision + 1,
      };
    });

  const empty = {
    windows: {},
    focused: null,
    topZ: 1,
    revision: 0,
    hydrated: false,
    unsaved: {},
    pendingOpen: null,
  } satisfies Partial<DesktopStore>;

  return {
    bounds: { width: 1280, height: 720 },
    deskTop: 0,
    ...empty,

    setBounds: (bounds) =>
      set((state) => {
        const from = state.bounds;
        if (from.width === bounds.width && from.height === bounds.height) return state;
        let changed = false;
        const windows = Object.fromEntries(
          (Object.entries(state.windows) as [AppId, WindowState][]).map(([id, window]) => {
            const frame = refit(id, window.frame, from, bounds);
            const restoreFrame = window.restoreFrame && clampFrame(id, window.restoreFrame, bounds);
            if (!sameFrame(frame, window.frame) || (restoreFrame && !sameFrame(restoreFrame, window.restoreFrame!))) changed = true;
            return [id, { ...window, frame, restoreFrame }];
          }),
        );
        return changed ? { bounds, windows, revision: state.revision + 1 } : { bounds };
      }),

    setDeskTop: (top) =>
      set((state) => {
        const delta = state.deskTop - top;
        if (delta === 0) return state;
        // The desk's top edge moved on screen; free windows move with the opposite offset to stay where they were.
        // Snapped windows are refitted by the bounds change that follows.
        let changed = false;
        const windows = Object.fromEntries(
          (Object.entries(state.windows) as [AppId, WindowState][]).map(([id, window]) => {
            if (snapZoneOf(window.frame, state.bounds)) return [id, window];
            const frame = clampFrame(id, { ...window.frame, y: window.frame.y + delta }, state.bounds);
            if (!sameFrame(frame, window.frame)) changed = true;
            return [id, { ...window, frame }];
          }),
        );
        return changed ? { deskTop: top, windows, revision: state.revision + 1 } : { deskTop: top };
      }),

    hydrate: (layout) => {
      const state = get();
      if (state.hydrated) return;
      const { bounds } = state;
      const windows: Partial<Record<AppId, WindowState>> = {};
      const saved = layout?.open ?? [];
      saved.forEach((id, index) => {
        const frame = layout?.frames[id] ?? homeFrame(id, bounds);
        const restore = layout?.restore?.[id];
        windows[id] = {
          frame: clampFrame(id, frame, bounds),
          z: index + 1,
          minimized: layout?.minimized?.includes(id) ?? false,
          restoreFrame: restore ? clampFrame(id, restore, bounds) : null,
          params: layout?.params?.[id] ?? {},
        };
      });
      // Windows the user opened while the layout was loading stay, on top of the restored ones.
      let topZ = saved.length;
      for (const [id, window] of Object.entries(state.windows) as [AppId, WindowState][]) {
        windows[id] = { ...window, z: ++topZ };
      }
      const opened = Object.keys(state.windows).length > 0;
      set({
        windows,
        topZ,
        focused: state.focused ?? topmostVisible(windows),
        hydrated: true,
        // Early openings are real changes the server has not seen yet.
        revision: opened ? state.revision + 1 : state.revision,
      });
    },

    reset: () => set({ ...empty }),

    setUnsaved: (id, unsaved) => {
      if (Boolean(get().unsaved[id]) === unsaved) return;
      set((state) => ({ unsaved: { ...state.unsaved, [id]: unsaved } }));
    },

    hasUnsaved: () => Object.values(get().unsaved).some(Boolean),

    confirmPendingOpen: () => {
      const pending = get().pendingOpen;
      if (!pending) return;
      set((state) => ({ pendingOpen: null, unsaved: { ...state.unsaved, [pending.id]: false } }));
      get().open(pending.id, pending.params);
    },

    cancelPendingOpen: () => set({ pendingOpen: null }),

    open: (id, params) => {
      const state = get();
      const existing = state.windows[id];
      if (existing) {
        if (params && state.unsaved[id] && !sameParams(params, existing.params)) {
          // Replacing the content would drop unsaved work: bring the window up and ask first.
          edit(id, () => ({ minimized: false, z: state.topZ + 1 }));
          set({ focused: id, topZ: state.topZ + 1, pendingOpen: { id, params } });
          return;
        }
        edit(id, (window) => ({ minimized: false, z: state.topZ + 1, params: params ?? window.params }));
        set({ focused: id, topZ: state.topZ + 1 });
        return;
      }
      set({
        windows: {
          ...state.windows,
          [id]: {
            frame: homeFrame(id, state.bounds),
            z: state.topZ + 1,
            minimized: false,
            restoreFrame: null,
            params: params ?? {},
          },
        },
        focused: id,
        topZ: state.topZ + 1,
        revision: state.revision + 1,
      });
    },

    setParams: (id, params) => edit(id, () => ({ params })),

    close: (id) =>
      set((state) => {
        const { [id]: _closed, ...rest } = state.windows;
        return {
          windows: rest,
          focused: topmostVisible(rest),
          revision: state.revision + 1,
          unsaved: { ...state.unsaved, [id]: false },
          pendingOpen: state.pendingOpen?.id === id ? null : state.pendingOpen,
        };
      }),

    focus: (id) => {
      const state = get();
      if (state.focused === id && state.windows[id]?.z === state.topZ) return;
      set((s) => ({
        windows: { ...s.windows, [id]: { ...s.windows[id]!, z: s.topZ + 1 } },
        focused: id,
        topZ: s.topZ + 1,
        // Stacking order is part of the saved layout.
        revision: s.revision + 1,
      }));
    },

    minimize: (id) => {
      edit(id, () => ({ minimized: true }));
      set((state) => ({ focused: topmostVisible(state.windows) }));
    },

    minimizeAll: () =>
      set((state) => ({
        windows: Object.fromEntries(
          Object.entries(state.windows).map(([id, window]) => [id, { ...window, minimized: true }]),
        ),
        focused: null,
        revision: state.revision + 1,
      })),

    toggleMaximize: (id) => {
      const { bounds } = get();
      edit(id, (window) =>
        window.restoreFrame
          ? { frame: window.restoreFrame, restoreFrame: null }
          : { frame: snapFrame('maximize', bounds), restoreFrame: window.frame },
      );
    },

    setFrame: (id, frame) => edit(id, () => ({ frame: clampFrame(id, frame, get().bounds), restoreFrame: null })),

    snap: (id, zone) => {
      const { bounds } = get();
      edit(id, (window) => ({ frame: snapFrame(zone, bounds), restoreFrame: window.restoreFrame ?? window.frame }));
    },

    arrange: () => {
      const { bounds } = get();
      set((state) => {
        const ordered = Object.entries(state.windows).sort(([, a], [, b]) => a.z - b.z);
        const windows = Object.fromEntries(
          ordered.map(([id, window]) => [
            id,
            { ...window, minimized: false, restoreFrame: null, frame: homeFrame(id as AppId, bounds) },
          ]),
        );
        return { windows, focused: (ordered.at(-1)?.[0] as AppId | undefined) ?? null, revision: state.revision + 1 };
      });
    },
  };
});

export function sameParams(a: AppParams, b: AppParams): boolean {
  return a.fileId === b.fileId && a.folderId === b.folderId;
}

function topmostVisible(windows: Partial<Record<AppId, WindowState>>): AppId | null {
  let best: AppId | null = null;
  let bestZ = -1;
  for (const id of APP_IDS) {
    const window = windows[id];
    if (window && !window.minimized && window.z > bestZ) {
      best = id;
      bestZ = window.z;
    }
  }
  return best;
}

export function toLayout(windows: Partial<Record<AppId, WindowState>>): DesktopLayout {
  const open = (Object.entries(windows) as [AppId, WindowState][]).sort(([, a], [, b]) => a.z - b.z);
  const params = open.filter(([, window]) => Object.keys(window.params).length > 0);
  const restorable = open.filter(([, window]) => window.restoreFrame);
  const minimized = open.filter(([, window]) => window.minimized);
  return {
    open: open.map(([id]) => id),
    frames: Object.fromEntries(open.map(([id, window]) => [id, window.frame])),
    ...(restorable.length ? { restore: Object.fromEntries(restorable.map(([id, window]) => [id, window.restoreFrame])) } : {}),
    ...(minimized.length ? { minimized: minimized.map(([id]) => id) } : {}),
    ...(params.length ? { params: Object.fromEntries(params.map(([id, window]) => [id, window.params])) } : {}),
  };
}
